import React, { useState, useRef, useEffect } from 'react';
import compassImage from '../assets/fengshui-compass.svg';

interface InteractiveCompassProps {
  className?: string;
  style?: React.CSSProperties;
  initialRotation?: number;
  onRotationChange?: (rotation: number) => void;
}

/**
 * 交互式可拖动旋转罗盘组件
 * 置于户型图下层，支持拖动调整朝向
 */
const InteractiveCompass: React.FC<InteractiveCompassProps> = ({
  className,
  style,
  initialRotation = 0,
  onRotationChange,
}) => {
  const [rotation, setRotation] = useState(initialRotation);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startAngleRef = useRef(0);
  const currentRotationRef = useRef(0);

  // 计算点击位置相对于中心的角度
  const getAngle = (clientX: number, clientY: number): number => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(clientY - centerY, clientX - centerX) * 180 / Math.PI;
  };

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const angle = getAngle(clientX, clientY);
    startAngleRef.current = angle - rotation;
    e.preventDefault();
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as MouseEvent).clientX;
      clientY = (e as MouseEvent).clientY;
    }
    const angle = getAngle(clientX, clientY);
    const newRotation = angle - startAngleRef.current;
    setRotation(newRotation);
    currentRotationRef.current = newRotation;
    onRotationChange?.(newRotation);
    e.preventDefault();
  };

  const handleEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    setRotation(initialRotation);
    currentRotationRef.current = initialRotation;
  }, [initialRotation]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    } else {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  });

  return (
    <div
      ref={containerRef}
      className={`interactive-compass-container ${className || ''}`}
      style={{
        ...style,
      }}
    >
      {/* 罗盘可拖动层 - 放大让八卦填满，留出中心空白放户型图 */}
      <div
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        className="compass-rotor"
        style={{
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        <img
          src={compassImage}
          alt="罗盘"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'contain',
          }}
        />
      </div>

      <div
        className="compass-hint"
      >
        拖动旋转
      </div>
    </div>
  );
};

export default InteractiveCompass;
